import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Navigation } from 'swiper/modules';
import { MaterialIcons } from '@expo/vector-icons';
import 'swiper/css';
import 'swiper/css/navigation';
import './MessageSlider.css';
import { synthesizeChunk, getTTSToken, playAudio } from '../../../services/ttsService';
import { useTutor } from '../../../contexts/TutorContext';
import { useAudioState } from '../../../contexts/AudioContext';
import { AudioVisualizer } from './AudioVisualizer';

interface Message {
  text: string;
  type: 'user' | 'bot';
}

interface MessageSliderProps {
  messages: Message[];
  isTTSPlaying: boolean;
}

export const MessageSlider: React.FC<MessageSliderProps> = ({ messages, isTTSPlaying }) => {
  const { selectedTutor } = useTutor();
  const { setIsAudioPlaying, setVisualizerState, visualizerState } = useAudioState();

  const handleTranslate = (message: string) => {
    // Add translation logic here
    console.log('Translate:', message);
  };

  const handleListen = async (message: string) => {
    // Don't allow listening while TTS is playing
    if (isTTSPlaying) {
      return;
    }

    try {
      setIsAudioPlaying(true);
      setVisualizerState('playing');
      const token = await getTTSToken();
      const languageCode = selectedTutor?.language_code || 'en-US';
      
      const audioContent = await synthesizeChunk(
        message,
        token,
        languageCode,
        {
          voice: selectedTutor?.voice_id || `${languageCode}-Neural2-F`,
          speed: 1.0,
          pitch: 0.0,
          audioFormat: 'mp3'
        }
      );

      if (audioContent) {
        await playAudio(audioContent);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
    } finally {
      setIsAudioPlaying(false);
      setVisualizerState('default');
    }
  };

  // Group messages into pairs (question-answer)
  const messagePairs = messages.reduce((pairs: Message[][], message, index) => {
    if (message.type === 'user') {
      // Find the next bot message
      const nextMessage = messages[index + 1];
      if (nextMessage && nextMessage.type === 'bot') {
        pairs.push([message, nextMessage]);
      } else {
        pairs.push([message]);
      }
    }
    return pairs;
  }, []);

  // Get the last 3 pairs (6 messages)
  const lastThreePairs = messagePairs.slice(-3);

  return (
    <View style={styles.container}>
      <AudioVisualizer 
        stream={null} 
        color={visualizerState === 'playing' ? 'rgba(255, 255, 0, 0.2)' : 'rgba(129, 199, 132, 0.2)'} 
        width={100}
        height={100}
      />
      <Swiper
        direction={'vertical'}
        slidesPerView={1}
        spaceBetween={20}
        mousewheel={true}
        navigation={true}
        modules={[Mousewheel, Navigation]}
        className={`mySwiper ${isTTSPlaying ? 'disabled' : ''}`}
        style={{ pointerEvents: isTTSPlaying ? 'none' : 'auto' }}
      >
        {lastThreePairs.reverse().map((pair, index) => (
          <SwiperSlide key={index}>
            <div className="swiper-slide-content">
              <View style={styles.slideContent}>
                {pair.map((message, msgIndex) => (
                  <View
                    key={msgIndex}
                    style={[
                      styles.messageContainer,
                      message.type === 'user' ? styles.messageRight : styles.messageLeft,
                    ]}
                  >
                    <View style={styles.messageInner}>
                      <View
                        style={[
                          styles.messageText,
                          message.type === 'user' ? styles.userMessage : styles.aiMessage,
                        ]}
                      >
                        <Text style={{ 
                          color: message.type === 'user' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
                          fontStyle: 'italic'
                        }}>{message.text}</Text>
                        {message.type !== 'user' && (
                          <View style={styles.actionsContainer}>
                            <TouchableOpacity
                              style={styles.actionButton}
                              onPress={() => handleTranslate(message.text)}
                            >
                              <MaterialIcons name="translate" size={16} color="white" />
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[
                                styles.actionButton,
                                isTTSPlaying && styles.actionButtonDisabled
                              ]}
                              onPress={() => handleListen(message.text)}
                              disabled={isTTSPlaying}
                            >
                              <MaterialIcons 
                                name="volume-up" 
                                size={16} 
                                color={isTTSPlaying ? "rgba(255, 255, 255, 0.4)" : "white"} 
                              />
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    maxWidth: 400,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    overflow: 'hidden',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    '@media (max-width: 768px)': {
      maxWidth: '100%',
    },
  },
  slideContent: {
    display: 'flex',
    flexDirection: 'column-reverse',
    width: '100%',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 10,
    paddingRight: 10,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  messageContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
  },
  messageInner: {
    maxWidth: '100%',
    '@media (max-width: 768px)': {
      maxWidth: '92%',
    },
  },
  messageLeft: {
    justifyContent: 'flex-start',
  },
  messageRight: {
    justifyContent: 'flex-end',
  },
  messageText: {
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'left',
    wordBreak: 'break-word',
    position: 'relative',
    fontStyle: 'italic',
  },
  userMessage: {
    marginLeft: 'auto',
    '& $messageText': {
      color: 'rgba(255, 255, 255, 0.9)',
    },
  },
  aiMessage: {
    marginRight: 'auto',
    '& $messageText': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 6,
    justifyContent: 'flex-start',
    marginTop: 5,
    flexShrink: 0,
    '@media (max-width: 768px)': {
      gap: 4,
      marginTop: 4,
    },
  },
  actionButton: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    '@media (max-width: 768px)': {
      width: 18,
      height: 18,
      borderRadius: 9,
    },
  },
  actionButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

export default MessageSlider;
